import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { FieldLabel, FIELD_INPUT, CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import type { Client } from '@/types/database';
import { ArrowLeft, Loader2, MapPin, Trash2, Copy, History, User, IdCard, Phone, Mail, Cake, Camera, Upload, X } from 'lucide-react';
import { StorageImage } from '@/components/ui/StorageImage';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { useCamera } from '@/hooks/useCamera';
import { toast } from 'sonner';
import { ClientSummaryTeaser } from '@/components/clients/ClientSummaryTeaser';
import { useHaptics } from '@/hooks/useHaptics';
import { cacheClients, getCachedClients } from '@/lib/offlineDataCache';
import { addToOfflineQueue, removeFromOfflineQueue } from '@/lib/offlineStorage';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useRole } from '@/hooks/useRole';
import { onlyDigits, formatDocument, formatCep, isValidDocument, detectDocumentKind } from '@/lib/documentUtils';
import { lookupCep } from '@/lib/cepLookup';
// Fonte única da linha de endereço — a MESMA usada pelo formulário de orçamento,
// que também cadastra cliente (src/lib/clientAddress.ts).
import { composeAddressLine } from '@/lib/clientAddress';

export default function ClientFormPage() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { successFeedback, warningFeedback } = useHaptics();
  const { isOnline, checkOnline, saveClientOffline } = useOfflineSync();
  const { canWrite, canDelete } = useRole();
  const { uploadFile, uploadPhoto, isUploading: isUploadingPhoto } = usePhotoUpload();
  const { capturePhoto, isCapturing } = useCamera();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'history'>('info');
  const [loadingClient, setLoadingClient] = useState(isEdit);

  const [editingClient, setEditingClient] = useState<Client | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [document, setDocument] = useState('');
  const [email, setEmail] = useState('');
  const [cep, setCep] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [municipioNome, setMunicipioNome] = useState('');
  const [municipioIbge, setMunicipioIbge] = useState('');
  const [uf, setUf] = useState('');
  const [inscricaoEstadual, setInscricaoEstadual] = useState('');
  /** `clients.birth_date` — DATE 'YYYY-MM-DD', sem hora (#54/#114). */
  const [birthDate, setBirthDate] = useState('');
  /** `clients.photo_url` — caminho no Storage privado; a leitura assina (#6). */
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [cepLoading, setCepLoading] = useState(false);

  // Load client for edit: prefer cache first (offline-friendly), then server.
  useEffect(() => {
    if (!id || !user) return;

    const applyClient = (client: Client) => {
      setEditingClient(client);
      setName(client.name);
      setPhone(client.phone || '');
      setDocument(client.document || '');
      setEmail(client.email || '');
      setCep(client.cep || '');
      const hasStructured = Boolean(
        client.logradouro || client.bairro || client.municipio_nome || client.numero,
      );
      setLogradouro(client.logradouro || (hasStructured ? '' : client.address || ''));
      setNumero(client.numero || '');
      setComplemento(client.complemento || '');
      setBairro(client.bairro || '');
      setMunicipioNome(client.municipio_nome || '');
      setMunicipioIbge(client.municipio_ibge || '');
      setUf(client.uf || '');
      setInscricaoEstadual(client.inscricao_estadual || '');
      // Metade da LEITURA (#70): perder a hidratacao aqui faz o proximo save
      // apagar a foto e o aniversario, com toast de sucesso e sem erro nenhum.
      setBirthDate(client.birth_date || '');
      setPhotoUrl(client.photo_url || null);
      setLoadingClient(false);
    };

    const cached = (getCachedClients(user.id) || []) as Client[];
    const cachedMatch = cached.find((c) => c.id === id);
    if (cachedMatch) {
      applyClient(cachedMatch);
    }

    if (isOnline) {
      supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .maybeSingle()
        .then(({ data, error }) => {
          if (data && !error) {
            applyClient(data as Client);
          } else if (!cachedMatch) {
            setLoadingClient(false);
          }
        });
    } else if (!cachedMatch) {
      setLoadingClient(false);
    }
  }, [id, user, isOnline]);

  const handleCepLookup = async (rawCep: string) => {
    const digits = onlyDigits(rawCep);
    if (digits.length !== 8) return;
    if (!isOnline) return;
    setCepLoading(true);
    try {
      const addr = await lookupCep(digits);
      if (addr) {
        if (addr.logradouro) setLogradouro(addr.logradouro);
        if (addr.bairro) setBairro(addr.bairro);
        if (addr.municipioNome) setMunicipioNome(addr.municipioNome);
        if (addr.uf) setUf(addr.uf);
        if (addr.municipioIbge) setMunicipioIbge(addr.municipioIbge);
      } else {
        toast.warning('CEP não encontrado.');
      }
    } finally {
      setCepLoading(false);
    }
  };

  const buildClientColumns = () => {
    const docDigits = onlyDigits(document);
    return {
      name: name.trim(),
      phone: phone.trim() || null,
      document: docDigits || null,
      email: email.trim() || null,
      cep: onlyDigits(cep) || null,
      logradouro: logradouro.trim() || null,
      numero: numero.trim() || null,
      complemento: complemento.trim() || null,
      bairro: bairro.trim() || null,
      municipio_nome: municipioNome.trim() || null,
      municipio_ibge: municipioIbge.trim() || null,
      uf: uf.trim().toUpperCase() || null,
      inscricao_estadual: inscricaoEstadual.trim() || null,
      // Ausencia e null, nunca string vazia: '' num DATE do Postgres e erro
      // de sintaxe, e '' num text seria "tem foto, e ela e nada".
      birth_date: birthDate || null,
      photo_url: photoUrl || null,
      address: composeAddressLine({
        logradouro, numero, bairro, municipioNome, uf,
      }) || null,
    };
  };

  /*
   * FOTO DO CLIENTE — bucket PRIVADO, leitura por URL assinada (#6).
   *
   * Offline a imagem fica como `data:` no estado e sobe junto com o cadastro
   * quando a fila sincroniza — o mesmo caminho da logo da empresa. Sem isso,
   * tirar foto sem sinal daria erro de upload no meio do cadastro.
   */
  const handlePhotoCapture = async () => {
    const result = await capturePhoto({ quality: 80, source: 'prompt' });
    if (!result?.dataUrl) return;

    if (!isOnline) {
      setPhotoUrl(result.dataUrl);
      toast.success('Foto salva localmente! O envio acontece ao sincronizar.', { icon: '📴' });
      return;
    }

    const uploaded = await uploadPhoto(result.dataUrl, 'quote-photos', 'clients', user?.id);
    if (uploaded) setPhotoUrl(uploaded.url);
  };

  const handlePhotoFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast.warning('Selecione uma imagem válida');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.warning('A imagem deve ter no máximo 2MB');
      return;
    }

    if (!isOnline) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoUrl(e.target?.result as string);
        toast.success('Foto salva localmente! O envio acontece ao sincronizar.', { icon: '📴' });
      };
      reader.onerror = () => toast.error('Erro ao carregar a imagem');
      reader.readAsDataURL(file);
      return;
    }

    const uploaded = await uploadFile(file, 'quote-photos', 'clients', user?.id);
    if (uploaded) setPhotoUrl(uploaded.url);
  };

  const handleSave = async () => {
    if (!canWrite) {
      toast.warning('Você tem acesso somente leitura nesta empresa.', { id: 'readonly-access' });
      return;
    }
    if (!name.trim()) {
      toast.warning('Nome é obrigatório');
      return;
    }

    if (isValidDocument(document) === false) {
      toast.warning('CPF/CNPJ inválido. Confira os dígitos ou deixe o campo em branco.');
      return;
    }

    setSaving(true);

    const clientColumns = buildClientColumns();

    const online = await checkOnline();

    if (editingClient) {
      if (!online) {
        setSaving(false);
        toast.warning('Não é possível editar cliente sem internet. Tente novamente quando estiver online.');
        return;
      }

      const { error } = await supabase
        .from('clients')
        .update(clientColumns)
        .eq('id', editingClient.id);

      setSaving(false);

      if (error) {
        toast.error('Erro ao atualizar cliente');
        return;
      }

      successFeedback();
      toast.success('Cliente atualizado!');
    } else {
      if (!online) {
        saveClientOffline({
          client: clientColumns,
          userId: user!.id,
          companyId: activeCompany?.id ?? null,
        });

        setSaving(false);
        successFeedback();
        window.dispatchEvent(new CustomEvent('clients-updated'));
        navigate('/clients');
        return;
      }

      const { error } = await supabase.from('clients').insert({
        user_id: user!.id,
        company_id: activeCompany?.id ?? null,
        ...clientColumns,
      });

      setSaving(false);

      if (error) {
        toast.error('Erro ao salvar cliente');
        return;
      }

      successFeedback();
      toast.success('Cliente salvo!');
    }

    window.dispatchEvent(new CustomEvent('clients-updated'));
    navigate('/clients');
  };

  const handleDelete = async () => {
    if (!editingClient) return;
    if (!canDelete) {
      toast.warning('Apenas o proprietário e administradores podem excluir.');
      return;
    }

    setDeleting(true);

    const online = await checkOnline();
    if (!online && user) {
      if (String(editingClient.id).startsWith('offline_')) {
        removeFromOfflineQueue(editingClient.id);
      } else {
        addToOfflineQueue('delete_client', { clientId: editingClient.id, userId: user.id });
      }

      const cached = (getCachedClients(user.id) || []) as any[];
      const next = cached.filter((c) => c.id !== editingClient.id);
      cacheClients(user.id, next);
      window.dispatchEvent(new CustomEvent('offline-queue-updated'));
      window.dispatchEvent(new CustomEvent('clients-updated'));

      setDeleting(false);
      warningFeedback();
      toast.success('Cliente excluído localmente! Será sincronizado quando houver internet.', { icon: '📴' });
      setDeleteDialogOpen(false);
      navigate('/clients');
      return;
    }

    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', editingClient.id);

    setDeleting(false);

    if (error) {
      toast.error('Erro ao excluir cliente');
      return;
    }

    warningFeedback();
    toast.success('Cliente excluído!');
    setDeleteDialogOpen(false);
    window.dispatchEvent(new CustomEvent('clients-updated'));
    navigate('/clients');
  };

  const handleDuplicate = async () => {
    if (!editingClient) return;

    setSaving(true);

    const { error } = await supabase.from('clients').insert({
      user_id: user!.id,
      company_id: activeCompany?.id ?? editingClient.company_id ?? null,
      name: `${editingClient.name} (cópia)`,
      phone: editingClient.phone,
      address: editingClient.address,
      document: editingClient.document,
      email: editingClient.email,
      cep: editingClient.cep,
      logradouro: editingClient.logradouro,
      numero: editingClient.numero,
      complemento: editingClient.complemento,
      bairro: editingClient.bairro,
      municipio_nome: editingClient.municipio_nome,
      municipio_ibge: editingClient.municipio_ibge,
      uf: editingClient.uf,
      inscricao_estadual: editingClient.inscricao_estadual,
    });

    setSaving(false);

    if (error) {
      toast.error('Erro ao duplicar cliente');
      return;
    }

    toast.success('Cliente duplicado!');
    window.dispatchEvent(new CustomEvent('clients-updated'));
    navigate('/clients');
  };

  if (!user) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <User className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Área de clientes</h2>
          <p className="mb-4 text-muted-foreground">
            Faça login para gerenciar seus clientes
          </p>
          <Button3D onClick={() => navigate('/auth')}>Fazer login</Button3D>
        </div>
      </MainLayout>
    );
  }

  const docKind = detectDocumentKind(document);
  const docValid = isValidDocument(document);

  /** Endereço já preenchido, resumido no gatilho do accordion (ver abaixo). */
  const addressSummary = composeAddressLine({ logradouro, numero, bairro, municipioNome, uf });

  const clientFormFields = (
    <div className="space-y-3">
      {/* IDENTIFICAÇÃO — o que o prestador precisa para cobrar e falar com o
          cliente. Fica em cartão aberto: é o caminho do cadastro rápido. */}
      <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
        {/* FOTO — avatar no topo, como um cadastro de contato. Fica acima do
            nome porque e o primeiro reconhecimento visual da linha na lista.
            Bucket PRIVADO: `StorageImage` assina a URL (#6). */}
        <div className="flex items-center gap-3">
          <div className="relative shrink-0">
            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border bg-muted">
              {photoUrl ? (
                <StorageImage
                  src={photoUrl}
                  alt={name || 'Foto do cliente'}
                  className="h-16 w-16 object-cover"
                  fallback={<User className="h-7 w-7 text-muted-foreground" />}
                />
              ) : (
                <User className="h-7 w-7 text-muted-foreground" />
              )}
            </div>
            {photoUrl && (
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className="absolute -right-1 -top-1 h-6 w-6 rounded-full shadow"
                onClick={() => setPhotoUrl(null)}
                aria-label="Remover foto"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoFile}
              className="hidden"
              id="client-photo-upload"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={handlePhotoCapture}
              disabled={isCapturing || isUploadingPhoto}
              aria-label="Tirar foto do cliente"
            >
              {isCapturing || isUploadingPhoto ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Camera className="h-4 w-4" />
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploadingPhoto}
              aria-label="Escolher foto do cliente"
            >
              {isUploadingPhoto ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <div className="space-y-1.5">
          <FieldLabel icon={User} htmlFor="name">Nome / Razão social *</FieldLabel>
          <Input
            id="name"
            placeholder="Nome do cliente"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={FIELD_INPUT}
          />
        </div>

        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <FieldLabel icon={IdCard} htmlFor="document">CPF / CNPJ</FieldLabel>
            <Input
              id="document"
              inputMode="numeric"
              placeholder="CPF ou CNPJ"
              value={formatDocument(document)}
              onChange={(e) => setDocument(onlyDigits(e.target.value))}
              className={cn(FIELD_INPUT, docValid === false && 'border-destructive')}
            />
            {docKind && docValid !== false && (
              <p className="text-xs text-muted-foreground">
                {docKind === 'cpf' ? 'CPF' : 'CNPJ'} detectado.
              </p>
            )}
            {docValid === false && <p className="text-xs text-destructive">Dígitos não conferem.</p>}
          </div>
          <div className="space-y-1.5">
            <FieldLabel icon={Phone} htmlFor="phone">WhatsApp / telefone</FieldLabel>
            <Input
              id="phone"
              placeholder="(00) 00000-0000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={FIELD_INPUT}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <FieldLabel icon={Mail} htmlFor="email">E-mail</FieldLabel>
          <Input
            id="email"
            type="email"
            placeholder="cliente@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={FIELD_INPUT}
          />
        </div>

        {/* ANIVERSARIO — vira evento na Agenda (`buildCalendarEvents`), que e o
            que torna o campo util: guardado sem aparecer em lugar nenhum, ele
            seria so mais um campo para preencher.

            `type="date"` devolve 'YYYY-MM-DD', exatamente o formato da coluna
            DATE — nao passa por `new Date` em momento nenhum, que e o que evita
            o dia anterior em UTC-3 (#54/#114). */}
        <div className="space-y-1.5">
          <FieldLabel icon={Cake} htmlFor="birth-date">Aniversário</FieldLabel>
          <Input
            id="birth-date"
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            className={FIELD_INPUT}
          />
          <p className="text-xs text-muted-foreground">
            Aparece na Agenda todo ano, no dia.
          </p>
        </div>
      </div>

      {/* ENDEREÇO — oito campos que só a NFS-e exige. Recolhido para o cadastro
          rápido (nome + telefone) não custar uma tela de rolagem, no mesmo
          formato dos blocos secundários do orçamento. O resumo no gatilho existe
          para o endereço já preenchido não PARECER perdido ao reabrir o
          cadastro: fechado e mudo, o usuário acha que sumiu. */}
      <Accordion type="single" collapsible>
        <AccordionItem value="endereco" className={cn(CONTENT_CARD, 'overflow-hidden px-4')}>
          <AccordionTrigger className="py-3.5 text-sm font-medium hover:no-underline">
            <span className="flex min-w-0 items-center gap-2">
              <MapPin className="h-4 w-4 shrink-0 text-primary" />
              <span className="min-w-0 text-left">
                Endereço
                <span className="ml-1 font-normal text-muted-foreground">(para nota fiscal)</span>
                {addressSummary && (
                  <span className="block truncate text-xs font-normal text-muted-foreground">
                    {addressSummary}
                  </span>
                )}
              </span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-4">
            <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="cep">CEP</FieldLabel>
                <div className="relative">
                  <Input
                    id="cep"
                    inputMode="numeric"
                    placeholder="00000-000"
                    value={formatCep(cep)}
                    onChange={(e) => setCep(onlyDigits(e.target.value))}
                    onBlur={(e) => handleCepLookup(e.target.value)}
                    className={FIELD_INPUT}
                  />
                  {cepLoading && (
                    <Loader2 className="absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                  )}
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="logradouro">Logradouro</FieldLabel>
                <Input
                  id="logradouro"
                  placeholder="Rua, avenida..."
                  value={logradouro}
                  onChange={(e) => setLogradouro(e.target.value)}
                  className={FIELD_INPUT}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="numero">Número</FieldLabel>
                <Input
                  id="numero"
                  placeholder="123"
                  value={numero}
                  onChange={(e) => setNumero(e.target.value)}
                  className={FIELD_INPUT}
                />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="complemento">Complemento</FieldLabel>
                <Input
                  id="complemento"
                  placeholder="Apto, sala, bloco..."
                  value={complemento}
                  onChange={(e) => setComplemento(e.target.value)}
                  className={FIELD_INPUT}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="bairro">Bairro</FieldLabel>
              <Input
                id="bairro"
                placeholder="Bairro / distrito"
                value={bairro}
                onChange={(e) => setBairro(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>

            <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-3">
              <div className="space-y-1.5 sm:col-span-2">
                <FieldLabel htmlFor="municipio">Município</FieldLabel>
                <Input
                  id="municipio"
                  placeholder="Cidade"
                  value={municipioNome}
                  onChange={(e) => setMunicipioNome(e.target.value)}
                  className={FIELD_INPUT}
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="uf">UF</FieldLabel>
                <Input
                  id="uf"
                  maxLength={2}
                  placeholder="UF"
                  value={uf}
                  onChange={(e) => setUf(e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))}
                  className={FIELD_INPUT}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="ie">Inscrição estadual</FieldLabel>
              <Input
                id="ie"
                placeholder="ISENTO para consumidor final"
                value={inscricaoEstadual}
                onChange={(e) => setInscricaoEstadual(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );

  /* ==================================================================== */
  /* HERO — o mesmo cabeçalho sangrado de /quotes/new.                     */
  /* -------------------------------------------------------------------- */
  /* Onde o orçamento mostra o TOTAL ao vivo, aqui vai o NOME ao vivo: é o  */
  /* valor que o formulário está construindo e o que identifica o cadastro. */
  const hero = (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">
            {!canWrite ? 'Somente leitura' : editingClient ? 'Editando' : 'Novo cadastro'}
          </p>
          <h1 className="truncate text-lg font-bold leading-tight">Cliente</h1>
        </div>

        {/* Duplicar e excluir só existem no cadastro já salvo, e são ÍCONES no
            cabeçalho — o mesmo lugar da tela de editar orçamento. */}
        {editingClient && canWrite && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
            onClick={handleDuplicate}
            disabled={saving}
            aria-label="Duplicar cliente"
            title="Duplicar cliente"
          >
            <Copy className="h-4 w-4" />
          </Button>
        )}
        {editingClient && canDelete && (
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-red-500/80 hover:text-white"
            onClick={() => setDeleteDialogOpen(true)}
            aria-label="Excluir cliente"
            title="Excluir cliente"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* O NOME fica aqui embaixo, em largura cheia — e não no `h1` lá de cima,
          que divide a linha com os botões de ação. Foi a lição do cabeçalho de
          editar orçamento (#159): com dois botões sobram ~196px, e "Construtora
          Vale Verde Empreendimentos" sairia cortado justamente no campo que
          identifica o cadastro. `line-clamp-2` em vez de `truncate` pela mesma
          razão: aqui o valor é texto livre, não um número. */}
      <div className="relative mt-5">
        <p className="line-clamp-2 text-2xl font-bold leading-tight">
          {name.trim() || <span className="text-white/60">Novo cliente</span>}
        </p>
        <p className="mt-1 truncate text-xs text-white/70">
          {[
            docKind && docValid !== false ? formatDocument(document) : null,
            phone.trim() || null,
          ]
            .filter(Boolean)
            .join(' · ') || 'Preencha os dados abaixo'}
        </p>
      </div>
    </div>
  );

  if (isEdit && loadingClient) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="pb-4">
          {hero}
          <div className="relative z-10 -mt-9 flex justify-center rounded-2xl border border-border/60 bg-card py-16 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-4">
        {hero}

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero */}
        <div className="relative z-10 -mt-9 space-y-5">
          {editingClient ? (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'info' | 'history')}>
              {/* Alternador de aba herda o estilo AZUL do componente base (#25):
                  aba não é botão de ação e por isso não usa o verde do app. */}
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="info" className="gap-2">
                  <User className="h-4 w-4" />
                  Dados
                </TabsTrigger>
                <TabsTrigger value="history" className="gap-2">
                  <History className="h-4 w-4" />
                  Histórico
                </TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="mt-4">
                {clientFormFields}
              </TabsContent>

              <TabsContent value="history" className="mt-4">
                <ClientSummaryTeaser clientId={editingClient.id} onNavigate={() => {}} />
              </TabsContent>
            </Tabs>
          ) : (
            clientFormFields
          )}
        </div>

        {/*
          Barra FLUTUANTE acima da navegação inferior — a mesma da tela de
          orçamento. Precisa ser filha DIRETA do container da página: `sticky` só
          desliza dentro do próprio pai.

          Some inteira na aba Histórico (não há o que salvar ali) e para quem só
          lê — barra vazia colada na navegação seria só ruído.
        */}
        {canWrite && (!editingClient || activeTab === 'info') && (
          <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
            <Button3D size="sm" className="w-full" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : editingClient ? (
                'Atualizar cliente'
              ) : (
                'Salvar cliente'
              )}
            </Button3D>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir cliente?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O cliente "{editingClient?.name}" será removido permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Excluindo...
                </>
              ) : (
                'Excluir'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainLayout>
  );
}
